import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { LifeBuoy, Send, Loader2, Clock } from "lucide-react";

const STATUS_STYLE = {
  pending:  { bg: "rgba(212,175,55,0.16)", color: "#8a6b12", label: "Pending review" },
  approved: { bg: "rgba(37,211,102,0.14)", color: "#0A7A2C", label: "Approved" },
  rejected: { bg: "rgba(220,53,69,0.12)", color: "#a11d29", label: "Rejected" },
};

export default function HardshipPanel() {
  const [reason, setReason] = useState("");
  const [months, setMonths] = useState(3);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/log/hardship-requests");
      setItems(data.requests || []);
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!reason.trim()) return toast.error("Please describe your situation.");
    setBusy(true);
    try {
      await api.post("/log/hardship-request", { reason, months_requested: months, contact_pref: "email" });
      toast.success("Request submitted. We will review within 3 working days.");
      setReason(""); setMonths(3);
      await load();
    } catch (e) {
      toast.error("Could not submit request.");
    } finally { setBusy(false); }
  };

  return (
    <div className="mv-card p-6" data-testid="hardship-panel">
      <div className="flex items-center gap-3 mb-4">
        <div className="w-10 h-10 rounded-2xl flex items-center justify-center mv-green-text" style={{ background: "rgba(212,175,55,0.16)" }}>
          <LifeBuoy size={18}/>
        </div>
        <div>
          <div className="mv-eyebrow"><span style={{ color: "var(--mv-gold)" }}>Hardship Pause</span></div>
          <div className="font-display text-lg mv-green-text">Pause payments up to 12 months — no penalties.</div>
        </div>
      </div>
      <p className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>
        Life changes. If your income drops, request a pause. Reviewed within 3 working days by MAVULA — you keep your ownership progress.
      </p>

      <form onSubmit={submit} className="mt-5 space-y-3">
        <label className="block">
          <span className="text-[12px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Months to pause</span>
          <select
            value={months} onChange={(e) => setMonths(parseInt(e.target.value))}
            className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"
            data-testid="hardship-months"
          >
            {[1,2,3,4,5,6,7,8,9,10,11,12].map((m) => <option key={m} value={m}>{`${m} month${m > 1 ? "s" : ""}`}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[12px] uppercase tracking-[0.18em]" style={{ color: "var(--mv-muted)" }}>Reason (kept confidential)</span>
          <textarea
            value={reason} onChange={(e) => setReason(e.target.value)} rows={3}
            placeholder="e.g. Job change, medical costs, family emergency…"
            className="mt-1 w-full px-3 py-2 rounded-xl border mv-hairline text-[14px]"
            data-testid="hardship-reason"
            required
          />
        </label>
        <button type="submit" className="btn-green" disabled={busy} data-testid="hardship-submit">
          {busy ? <Loader2 className="animate-spin" size={16}/> : <Send size={16}/>}
          {busy ? "Sending…" : "Request Pause"}
        </button>
      </form>

      {/* Past requests */}
      <div className="mt-6 pt-5 border-t mv-hairline">
        <div className="text-[11.5px] uppercase tracking-[0.2em] mb-3" style={{ color: "var(--mv-muted)" }}>Your requests</div>
        {loading ? (
          <div className="text-neutral-500 text-[13.5px]">Loading…</div>
        ) : items.length === 0 ? (
          <div className="text-[13.5px]" style={{ color: "var(--mv-muted)" }}>No requests yet.</div>
        ) : (
          <ul className="space-y-2" data-testid="hardship-history">
            {items.map((r) => {
              const s = STATUS_STYLE[r.status] || STATUS_STYLE.pending;
              return (
                <li key={r.id} className="flex justify-between items-start gap-3 text-[13.5px] border-b mv-hairline pb-2">
                  <div>
                    <div className="font-semibold mv-green-text">{r.months_requested} month{r.months_requested > 1 ? "s" : ""}</div>
                    <div className="text-[12.5px]" style={{ color: "var(--mv-muted)" }}>{r.reason}</div>
                    <div className="text-[11px] mt-1 flex items-center gap-1" style={{ color: "var(--mv-muted)" }}>
                      <Clock size={11}/> {(r.created_at || "").slice(0,10)}
                    </div>
                    {r.admin_note && <div className="text-[12px] mt-1 italic">Note: {r.admin_note}</div>}
                  </div>
                  <span className="text-[11px] px-2 py-1 rounded-full whitespace-nowrap"
                        style={{ background: s.bg, color: s.color }}>{s.label}</span>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}
